import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useOffline } from '@/contexts/OfflineContext';
import { getFromOfflineStore } from '@/lib/offlineDb';

export interface Site {
  id: string;
  name: string;
  address?: string;
  phone?: string;
  capacity?: number;
  is_active?: boolean;
  is_family_shelter?: boolean;
  created_at?: string;
  updated_at?: string;
}

export const useSites = () => {
  const queryClient = useQueryClient();
  const { isOnline, cacheData } = useOffline();

  // Set up real-time subscription
  useEffect(() => {
    if (!isOnline) return;
    
    console.log('Setting up sites realtime subscription');
    
    const channel = supabase
      .channel('sites-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sites'
        },
        (payload) => {
          console.log('Sites table changed:', payload);
          queryClient.invalidateQueries({ queryKey: ['sites'] });
        }
      )
      .subscribe();

    return () => {
      console.log('Cleaning up sites realtime subscription');
      supabase.removeChannel(channel);
    };
  }, [queryClient, isOnline]);

  const { data: sites = [], isLoading } = useQuery({
    queryKey: ['sites'],
    queryFn: async () => {
      if (!isOnline) {
        console.log('Offline: Loading sites from cache');
        return getFromOfflineStore<Site>('sites');
      }

      const { data, error } = await supabase
        .from('sites')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Cache data for offline use
      if (data) {
        await cacheData('sites', data);
      }
      
      return data as Site[];
    },
    staleTime: isOnline ? 30000 : Infinity
  });

  return { sites, isLoading };
};
